import { Collapse, Grid } from "@mui/material";
import classNames from "classnames";
import TextButton from "components/shared/TextButton";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import type { FieldData } from "formFields";
import type { SetStateAction } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ICustomField } from "store/customFieldsStore";

import { getEntityTypeOptions } from "../CustomFieldForm";
import styles from "../style.module.scss";

interface ICustomFieldLine {
    /**
     * Field with its data.
     */
    field: ICustomField;
    /**
     * Options on the menu button.
     */
    fieldMenuOptions: { id: string; name: string; action: () => void }[];
    /**
     * Field on which the menu button was clicked.
     */
    chosenField: ICustomField | null;
    /**
     * Sets data for chosenField.
     */
    setChosenField: (value: SetStateAction<ICustomField | null>) => void;
    /**
     * Section on which the menu button was clicked.
     */
    section: FieldData;
    /**
     * Sets data for section.
     */
    setChosenSection: (value: SetStateAction<string>) => void;
    /**
     * Handles opening and closing of collapse that shows details of field.
     */
    handleCollapse: (field: ICustomField, buttonType: string) => void;
    /**
     * Type of field.
     */
    valueType: string | undefined;
    /**
     * Determines whether button or line has been click (used to stop propagation).
     */
    hasCollapseBeenTriggered: boolean;
}

// In Custom Fields settings, the line specific to a custom field. Includes actions and collapse for details.
const CustomFieldLine = ({
    field,
    fieldMenuOptions,
    chosenField,
    setChosenField,
    section,
    setChosenSection,
    handleCollapse,
    valueType,
    hasCollapseBeenTriggered,
}: ICustomFieldLine) => {
    const [t] = useTranslation();
    const isOpen = field?.id === chosenField?.id && hasCollapseBeenTriggered;

    const entityLabel = getEntityTypeOptions(t).find((option) => option.value === field.entityType)?.label ?? "";

    return (
        <div
            key={field.uuid}
            className={styles["form-custom-field"]}
            id={`${field?.name.replace(" ", "_")}_${field?.id}`}
            onClick={() => handleCollapse(field, "details")}
        >
            <div className={styles["left-line"]}></div>
            <div className={styles["field-with-collapse"]}>
                <div className={styles["content"]}>
                    <div className={styles["content-description"]}>
                        <span>{field.name}</span>
                    </div>
                    <div className={styles["custom-field-actions"]}>
                        <TextButton
                            text={
                                isOpen
                                    ? t("CustomFields.CollapseInfo.HideDetail")
                                    : t("CustomFields.CollapseInfo.ShowDetail")
                            }
                            handleClick={() => handleCollapse(field, "details")}
                            style={{ fontSize: "0.75rem" }}
                        />
                        <ThreeDotMenu
                            options={fieldMenuOptions}
                            onMenuClick={() => {
                                setChosenField(field);
                                setChosenSection(section?.key);
                            }}
                        />
                    </div>
                </div>
                <Collapse in={isOpen} timeout={500} unmountOnExit key={field.uuid}>
                    <div className={styles["collapse-content"]}>
                        <span className={styles["info-title"]}>
                            {t("CustomFields.CollapseInfo.Description")}
                            <span>{field.helperText}</span>
                        </span>
                        <span className={styles["info-title"]}>
                            {t("CustomFields.CollapseInfo.ValueType")}
                            <span>{valueType ?? ""}</span>
                        </span>
                        {field.relatedResource === "beneficiary" && field.entityType !== "none" && (
                            <span className={styles["info-title"]}>
                                {t("CustomFields.CollapseInfo.Entity")}
                                <span>{entityLabel as string}</span>
                            </span>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                            {!!field.isRequired && (
                                <div
                                    className={classNames({
                                        [styles["custom-field-tag"]]: true,
                                    })}
                                >
                                    {t("CustomFields.CollapseInfo.IsRequired")}
                                </div>
                            )}
                            {!!field.showOnCreation && (
                                <div
                                    className={classNames({
                                        [styles["custom-field-tag"]]: true,
                                    })}
                                >
                                    {t("CustomFields.CollapseInfo.OnCreation")}
                                </div>
                            )}
                            {!!field.showOnPortal && (
                                <div
                                    className={classNames({
                                        [styles["custom-field-tag"]]: true,
                                    })}
                                >
                                    {t("CustomFields.CollapseInfo.ShowOnPortal")}
                                </div>
                            )}
                        </div>
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

export default CustomFieldLine;
